import Box from "@mui/material/Box";
import { paletteVar } from "@platform/design-system";
import { BenefitPlanTypeEnum, CoverageTypeEnum } from "@platform/openapi-types";
import { IHeaderGroupParams } from "ag-grid-community";
import React, { useEffect, useState } from "react";

import { useUpdateBenefitPlanGroup } from "../../../api/benefitPlanGroupMutations";

import {
  CurrentCarrierPolicySelect,
  clearAutoSelectGuard,
} from "./ContextMenu/CurrentCarrierPolicySelect";

/** Params supplied via `headerGroupComponentParams` for a current group. */
export interface CurrentPolicyGroupHeaderParams {
  coverageId: string;
  rfpId: string;
  coverageType: CoverageTypeEnum;
  productType: BenefitPlanTypeEnum;
  groupId: string;
  currentPolicyUuid?: string;
}

type CurrentPolicyGroupHeaderProps = IHeaderGroupParams &
  CurrentPolicyGroupHeaderParams;

/**
 * AG Grid group-header component for a current plan group: renders the
 * "Current Carrier - Policy" dropdown in place of the editable nickname and
 * persists the selection to the group (ADR 10.22).
 */
export const CurrentPolicyGroupHeader: React.FC<
  CurrentPolicyGroupHeaderProps
> = ({
  coverageId,
  rfpId,
  coverageType,
  productType,
  groupId,
  currentPolicyUuid,
}) => {
  const [value, setValue] = useState<string | undefined>(currentPolicyUuid);
  const updateGroup = useUpdateBenefitPlanGroup();

  // Re-sync when the persisted value changes (mutation settles + refetch, or an
  // external edit) so the dropdown never shows stale state.
  useEffect(() => {
    setValue(currentPolicyUuid);
  }, [currentPolicyUuid]);

  const handleChange = (policyUuid: string | undefined): void => {
    const previous = value;
    setValue(policyUuid); // optimistic

    updateGroup.mutate(
      {
        id: groupId,
        rfpId,
        coverageType,
        productType,
        ...(policyUuid
          ? { currentPolicyUuid: policyUuid }
          : { clearCurrentPolicy: true }),
      },
      {
        // Revert the optimistic selection if the PATCH fails, and clear the
        // auto-select guard so a single-candidate group can retry.
        onError: () => {
          setValue(previous);
          clearAutoSelectGuard(groupId);
        },
      },
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        // Current-group fill spanning the whole cell. Reads the same selected
        // token as the current-column zebra stripe so the highlight tracks the
        // colour scheme; the opaque fallback keeps the grey for themes without
        // the variable.
        backgroundColor: paletteVar("action-selected", "rgb(245, 248, 251)"),
      }}
    >
      <CurrentCarrierPolicySelect
        coverageId={coverageId}
        planType={productType}
        value={value}
        onChange={handleChange}
        disabled={updateGroup.isPending}
        autoSelectKey={groupId}
        inline
      />
    </Box>
  );
};

CurrentPolicyGroupHeader.displayName = "CurrentPolicyGroupHeader";
export default CurrentPolicyGroupHeader;
